import { BaseCompanyIdProps } from "@custom-types/base-types";
import { BaseMembersProps, MemberTypes } from "@custom-types/member-types";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useCallback, useMemo, useState } from "react";
import { TableType } from "@utils/track-event/track-event-list";
import { MemberHeaders } from "@components/table/members/members-table-utils";
import { getMembersColumns } from "@components/table/members/members-column/members-columns";
import { useDateTime } from "@hooks/use-date-time";
import { ReactNode } from "react";
import { TeamMemberBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import MembersDuoColorIcon from "@assets/icons/members-duo-color.svg?react";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import { API_MEMBERS_PER_REQUEST } from "@store/teams/teams-slice-utils";
import { FaroPaginatedTable } from "@components/common/faro-table/faro-paginated-table";

interface Props extends BaseCompanyIdProps, BaseMembersProps {
  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** All the columns that we want to show. The order defines the order of the columns */
  requiredColumns: MemberHeaders[];

  /** The team to display members for */
  team: SphereDashboardAPITypes.ITeam;

  /** Whether to hide the checkbox on each row or not */
  shouldHideCheckbox?: boolean;

  /** List of all the bulk actions */
  bulkActionButtons: TeamMemberBulkButtonComponents;

  /** The component that will render for the side panel */
  sidePanelContent?: ReactNode;

  /** The size of the side panel */
  sidePanelContentWidth?: string;

  /** The status of the side panel */
  isSidePanelOpened?: boolean;

  /** Callback for loading more items (for pagination) */
  loadMoreItems: () => Promise<void>;

  /** Whether there are more items to load */
  hasMoreItems: boolean;
}

/**
 * A table component specifically for displaying team members with pagination
 */
export function TeamMembersTable({
  companyId,
  members,
  isLoading,
  team,
  requiredColumns,
  bulkActionButtons,
  sidePanelContent,
  sidePanelContentWidth,
  isSidePanelOpened,
  shouldHideCheckbox = false,
  loadMoreItems,
  hasMoreItems,
}: Props): JSX.Element {
  const { isMedium, isScreenLgAndLarger } = useMediaQueryList();
  const { formatDate } = useDateTime();
  const [page, setPage] = useState<number>(0);

  // Definition of the displayed columns
  const columns = useMemo(() => {
    const columnsMap = getMembersColumns({
      companyId,
      tableSubject: "team",
      team,
      formatDate,
      options: {
        isScreenLgAndLarger,
        isMedium,
      },
    });

    return requiredColumns.map((header) => columnsMap[header]);
  }, [
    companyId,
    formatDate,
    isMedium,
    isScreenLgAndLarger,
    requiredColumns,
    team,
  ]);

  // Map entity to row
  const mapEntityToRow = useCallback((member: MemberTypes) => {
    return {
      id: member.identity,
      entity: member,
    };
  }, []);

  return (
    <FaroPaginatedTable
      entities={members as MemberTypes[]}
      columns={columns}
      isLoading={isLoading}
      mapEntityToRow={mapEntityToRow}
      itemsPerPage={API_MEMBERS_PER_REQUEST}
      hasMoreItems={hasMoreItems}
      loadMoreItems={loadMoreItems}
      page={page}
      setPage={setPage}
      bulkActionButtons={bulkActionButtons}
      shouldHideCheckbox={shouldHideCheckbox}
      emptyIcon={MembersDuoColorIcon}
      emptyTitle="No members to show"
      emptySubtitle=""
      sidePanelContent={sidePanelContent}
      sidePanelContentWidth={sidePanelContentWidth}
      isSidePanelOpened={isSidePanelOpened}
      tableType={TableType.teamMembers}
    />
  );
}
